import { useEffect } from 'react'
import { useMixpanel } from '@captaincause/lib/mixpanel'
import { Icon } from '../Icon'
import { Button } from '../Button'
import { tm } from '../tm'
import type { IconKeys, SizeKeys } from '../Icon'

export interface SocialMediaItem {
  href?: string
  icon: Extract<
    IconKeys,
    'instagram' | 'facebook' | 'x' | 'linkedin' | 'youtube' | 'link'
  >
  label?: string
  className?: string
}

interface SocialMediaProps {
  className?: string
  iconSize: SizeKeys
  id?: string
  socialsMedia: SocialMediaItem[]
}

export const SIZES: Partial<Record<SizeKeys, string>> = {
  normal: 'space-x-1 md:space-x-1',
  small: 'space-x-1 md:space-x-1',
}

export const SPACINGS: Partial<Record<SizeKeys, string>> = {
  normal: 'p-2',
  small: 'p-1',
}

export function SocialMedia({
  className = '',
  iconSize,
  id = '',
  socialsMedia,
}: SocialMediaProps) {
  const { trackLinks } = useMixpanel()

  useEffect(() => {
    socialsMedia
      .filter(socialMedia => !!socialMedia.href)
      .forEach(socialMedia =>
        trackLinks(
          `#social-media-${id ? `${id}-` : ''}${socialMedia.icon}`,
          `Click on Social Media`,
          { social_media: socialMedia.icon },
        ),
      )
  }, [id, trackLinks, socialsMedia])

  if (socialsMedia.every(socialMedia => !socialMedia.href)) return null

  return (
    <div
      data-testid="social-media-container"
      className={tm(
        'flex flex-row md:items-center',
        SIZES[iconSize],
        className,
      )}
    >
      {socialsMedia
        .filter(socialMedia => !!socialMedia.href)
        .map(socialMedia => (
          <Button
            key={socialMedia.href}
            id={`social-media-${id ? `${id}-` : ''}${socialMedia.icon}`}
            ariaLabel={socialMedia.label}
            variant="raw"
            href={socialMedia.href}
            className={tm(SPACINGS[iconSize], socialMedia.className)}
          >
            <Icon icon={socialMedia.icon} size={iconSize} />
          </Button>
        ))}
    </div>
  )
}
